import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { driverAPI, vehicleTypesAPI, serviceLocationsAPI } from '../../services/api';

// Get initial state
const getInitialState = () => {
  return {
    drivers: [],
    selectedDriver: null,
    vehicleDetails: null,
    driverDocuments: null,
    driverTasks: null,
    driverReviews: null,
    vehicleTypes: [],
    statistics: {
      totalDrivers: 0,
      activeDrivers: 0,
      completedJobs: 0,
      averageRating: 0
    },
    pagination: null,
    isLoading: false,
    tasksLoading: false,
    reviewsLoading: false,
    error: null,
    baseLocations: [],
baseLocationsLoading: false,
  };
};

// ==================== INVITE DRIVER ====================

export const inviteDriver = createAsyncThunk(
  'driver/invite',
  async (driverData, { rejectWithValue }) => {
    try {
      const response = await driverAPI.inviteDriver(driverData);
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.Error ||
        error.response?.data?.message ||
        error.response?.data?.Message ||
        'Failed to invite driver. Please try again.'
      );
    }
  }
);

// ==================== GET DRIVERS LIST ====================

export const getDriversList = createAsyncThunk(
  'driver/getList',
  async (page = 1, { rejectWithValue }) => {
    try {
      const response = await driverAPI.getDriversList(page);
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.Error ||
        error.response?.data?.message ||
        'Failed to fetch drivers list.'
      );
    }
  }
);

// ==================== GET SINGLE DRIVER ====================

export const getDriverById = createAsyncThunk(
  'driver/getById',
  async (driverId, { rejectWithValue }) => {
    try {
      const response = await driverAPI.getDriverById(driverId);
      console.log("response.data--", response.data)
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.Error ||
        error.response?.data?.message ||
        'Failed to fetch driver details.'
      );
    }
  }
);

// ==================== UPDATE DRIVER ====================

export const updateDriver = createAsyncThunk(
  'driver/update',
  async (driverData, { rejectWithValue }) => {
    try {
      // Call API directly with data - NO nesting
      const response = await driverAPI.updateDriver(driverData);
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.Error ||
        error.response?.data?.message ||
        error.response?.data?.Message ||
        'Failed to update driver.'
      );
    }
  }
);


export const fetchBaseLocations = createAsyncThunk(
  'driver/fetchBaseLocations',
  async (_, { rejectWithValue }) => {
    try {
      const response = await serviceLocationsAPI.getBaseLocationsList();
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.Error ||
        error.response?.data?.message ||
        'Failed to fetch base locations.'
      );
    }
  }
);

// ==================== VERIFY DRIVER (APPROVE/REJECT/SUSPEND) ====================

export const verifyDriver = createAsyncThunk(
  'driver/verify',
  async ({ driverId, accountStatus, availabilityStatus }, { rejectWithValue }) => {
    try {
      const payload = { DriverId: driverId };

      if (accountStatus) {
        payload.AccountStatus = accountStatus;
      }

      if (availabilityStatus) {
        payload.AvailabilityStatus = availabilityStatus;
      }

      const response = await driverAPI.verifyDriver(payload);
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.Error ||
        error.response?.data?.message ||
        'Failed to verify driver.'
      );
    }
  }
);

// ==================== GET DRIVER VEHICLE DETAILS ====================

export const getDriverVehicleDetails = createAsyncThunk(
  'driver/getVehicleDetails',
  async (driverId, { rejectWithValue }) => {
    try {
      const response = await driverAPI.getDriverVehicleDetails(driverId);
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.Error ||
        error.response?.data?.message ||
        'Failed to fetch vehicle details.'
      );
    }
  }
);

// ==================== UPDATE DRIVER VEHICLE DETAILS ====================

export const updateDriverVehicleDetails = createAsyncThunk(
  'driver/updateVehicleDetails',
  async (vehicleData, { rejectWithValue }) => {
    try {
      const response = await driverAPI.updateDriverVehicleDetails(vehicleData);
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.Error ||
        error.response?.data?.message ||
        'Failed to update vehicle details.'
      );
    }
  }
);

// ==================== GET DRIVER DOCUMENTS ====================

export const getDriverDocuments = createAsyncThunk(
  'driver/getDocuments',
  async (driverId, { rejectWithValue }) => {
    try {
      const response = await driverAPI.getDriverDocuments(driverId);
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.Error ||
        error.response?.data?.message ||
        'Failed to fetch documents.'
      );
    }
  }
);

// ==================== UPLOAD DRIVER DOCUMENTS ====================

export const uploadDriverDocuments = createAsyncThunk(
  'driver/uploadDocuments',
  async (formData, { rejectWithValue }) => {
    try {
      const response = await driverAPI.uploadDriverDocuments(formData);
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.Error ||
        error.response?.data?.message ||
        'Failed to upload documents.'
      );
    }
  }
);

// ==================== GET VEHICLE TYPES ====================

export const getVehicleTypes = createAsyncThunk(
  'driver/getVehicleTypes',
  async (_, { rejectWithValue }) => {
    try {
      const response = await vehicleTypesAPI.getVehicleTypes();
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.Error ||
        error.response?.data?.message ||
        'Failed to fetch vehicle types.'
      );
    }
  }
);

// ==================== GET DRIVER TASKS ====================

export const getDriverTasks = createAsyncThunk(
  'driver/getTasks',
  async ({ driverId, status }, { rejectWithValue }) => {
    try {
      const response = await driverAPI.getDriverTasks(driverId, { status });
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.Error ||
        error.response?.data?.message ||
        'Failed to fetch driver tasks.'
      );
    }
  }
);

// ==================== GET DRIVER REVIEWS ====================

export const getDriverReviews = createAsyncThunk(
  'driver/getReviews',
  async (driverId, { rejectWithValue }) => {
    try {
      const response = await driverAPI.getDriverReviews(driverId);
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.Error ||
        error.response?.data?.message ||
        'Failed to fetch driver reviews.'
      );
    }
  }
);

// ==================== DRIVER SLICE ====================

const driverSlice = createSlice({
  name: 'driver',
  initialState: getInitialState(),
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setSelectedDriver: (state, action) => {
      state.selectedDriver = action.payload;
    },
    clearDriverDetails: (state) => {
      state.selectedDriver = null;
      state.vehicleDetails = null;
      state.driverDocuments = null;
    },
    // Update statistics from drivers list
    updateStatistics: (state) => {
      if (state.drivers.length > 0) {
        state.statistics.totalDrivers = state.drivers.length;
        state.statistics.activeDrivers = state.drivers.filter(d => d.IsAvailable).length;
        state.statistics.completedJobs = state.drivers.reduce((sum, d) => sum + (d.TasksCompleted || 0), 0);

        const totalRating = state.drivers.reduce((sum, d) => sum + parseFloat(d.AverageRating || 0), 0);
        state.statistics.averageRating = (totalRating / state.drivers.length).toFixed(1);
      }
    },
    // Optimistic update for driver status toggle (APPROVED/SUSPENDED)
    toggleDriverStatusOptimistic: (state, action) => {
      const { driverId, newStatus } = action.payload;
      const driverIndex = state.drivers.findIndex(d => d.ID === driverId);
      if (driverIndex !== -1) {
        state.drivers[driverIndex].AccountStatus = newStatus;
      }
    },
    // Optimistic update for driver availability toggle
    toggleDriverAvailabilityOptimistic: (state, action) => {
      const { driverId, newAvailability } = action.payload;
      // Update in drivers list
      const driverIndex = state.drivers.findIndex(d => d.ID === driverId);
      if (driverIndex !== -1) {
        state.drivers[driverIndex].AvailabilityStatus = newAvailability;
      }
      // Update in selectedDriver if same
      if (state.selectedDriver?.ID === driverId) {
        state.selectedDriver.AvailabilityStatus = newAvailability;
      }
    }
  },
  extraReducers: (builder) => {
    // ==================== INVITE DRIVER ====================
    builder
      .addCase(inviteDriver.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(inviteDriver.fulfilled, (state, action) => {
        state.isLoading = false;
        state.error = null;
      })
      .addCase(inviteDriver.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });

      builder
  .addCase(fetchBaseLocations.pending, (state) => {
    state.baseLocationsLoading = true;
  })
  .addCase(fetchBaseLocations.fulfilled, (state, action) => {
    state.baseLocationsLoading = false;
    state.baseLocations = action.payload.BaseLocations || [];
  })
  .addCase(fetchBaseLocations.rejected, (state) => {
    state.baseLocationsLoading = false;
  });

    // ==================== GET DRIVERS LIST ====================
    builder
      .addCase(getDriversList.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getDriversList.fulfilled, (state, action) => {
        state.isLoading = false;
        state.drivers = action.payload.Drivers || [];
        state.pagination = action.payload.Pagination || null;
        state.error = null;

        // Calculate statistics from Summary
        if (action.payload.Summary) {
          state.statistics.totalDrivers = action.payload.Summary.TotalDrivers || 0;
          state.statistics.activeDrivers = action.payload.Summary.Approved || 0;

          // Calculate completed jobs and average rating from drivers array
          if (state.drivers.length > 0) {
            state.statistics.completedJobs = state.drivers.reduce(
              (sum, d) => sum + (d.TasksCompleted || 0),
              0
            );

            const totalRating = state.drivers.reduce(
              (sum, d) => sum + parseFloat(d.AverageRating || 0),
              0
            );
            state.statistics.averageRating = state.drivers.length > 0
              ? (totalRating / state.drivers.length).toFixed(1)
              : '0.0';
          }
        }
      })
      .addCase(getDriversList.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });

    // ==================== GET DRIVER BY ID ====================
    builder
      .addCase(getDriverById.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getDriverById.fulfilled, (state, action) => {
        state.isLoading = false;
        state.selectedDriver = action.payload.Driver || action.payload;
        state.error = null;
      })
      .addCase(getDriverById.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });

    // ==================== UPDATE DRIVER ====================
    builder
      .addCase(updateDriver.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateDriver.fulfilled, (state, action) => {
        state.isLoading = false;
        state.error = null;

        // Update driver in list
        const updatedDriver = action.payload.Driver;
        if (updatedDriver) {
          state.drivers = state.drivers.map(driver =>
            driver.ID === updatedDriver.ID ? updatedDriver : driver
          );

          // Update selected driver if it's the same one
          if (state.selectedDriver?.ID === updatedDriver.ID) {
            state.selectedDriver = updatedDriver;
          }
        }
      })
      .addCase(updateDriver.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });

    // ==================== VERIFY DRIVER ====================
    builder
      .addCase(verifyDriver.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(verifyDriver.fulfilled, (state, action) => {
        state.isLoading = false;
        state.error = null;

        // Update driver in list
        const updatedDriver = action.payload.Driver;
        if (updatedDriver) {
          state.drivers = state.drivers.map(driver =>
            driver.ID === updatedDriver.ID ? updatedDriver : driver
          );

          // Update selected driver if it's the same one
          if (state.selectedDriver?.ID === updatedDriver.ID) {
            state.selectedDriver = updatedDriver;
          }
        }
      })
      .addCase(verifyDriver.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });

    // ==================== GET VEHICLE DETAILS ====================
    builder
      .addCase(getDriverVehicleDetails.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getDriverVehicleDetails.fulfilled, (state, action) => {
        state.isLoading = false;
        state.vehicleDetails = action.payload.Vehicle || action.payload;
        state.error = null;
      })
      .addCase(getDriverVehicleDetails.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });

    // ==================== UPDATE VEHICLE DETAILS ====================
    builder
      .addCase(updateDriverVehicleDetails.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateDriverVehicleDetails.fulfilled, (state, action) => {
        state.isLoading = false;
        state.vehicleDetails = action.payload.Vehicle || action.payload;
        state.error = null;
      })
      .addCase(updateDriverVehicleDetails.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });

    // ==================== GET DOCUMENTS ====================
    builder
      .addCase(getDriverDocuments.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getDriverDocuments.fulfilled, (state, action) => {
        state.isLoading = false;
        state.driverDocuments = action.payload;
        state.error = null;
      })
      .addCase(getDriverDocuments.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });

    // ==================== UPLOAD DOCUMENTS ====================
    builder
      .addCase(uploadDriverDocuments.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(uploadDriverDocuments.fulfilled, (state, action) => {
        state.isLoading = false;
        state.error = null;

        // Update documents in state if response contains them
        if (action.payload.Documents) {
          state.driverDocuments = action.payload;
        }
      })
      .addCase(uploadDriverDocuments.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });

    // ==================== GET VEHICLE TYPES ====================
    builder
      .addCase(getVehicleTypes.pending, (state) => {
        state.error = null;
      })
      .addCase(getVehicleTypes.fulfilled, (state, action) => {
        state.vehicleTypes = action.payload.Data || [];
        state.error = null;
      })
      .addCase(getVehicleTypes.rejected, (state, action) => {
        state.error = action.payload;
      });

    // ==================== GET DRIVER TASKS ====================
    builder
      .addCase(getDriverTasks.pending, (state) => {
        state.tasksLoading = true;
        state.error = null;
      })
      .addCase(getDriverTasks.fulfilled, (state, action) => {
        state.tasksLoading = false;
        state.driverTasks = action.payload;
        state.error = null;
      })
      .addCase(getDriverTasks.rejected, (state, action) => {
        state.tasksLoading = false;
        state.error = action.payload;
      });

    // ==================== GET DRIVER REVIEWS ====================
    builder
      .addCase(getDriverReviews.pending, (state) => {
        state.reviewsLoading = true;
        state.error = null;
      })
      .addCase(getDriverReviews.fulfilled, (state, action) => {
        state.reviewsLoading = false;
        state.driverReviews = action.payload;
        state.error = null;
      })
      .addCase(getDriverReviews.rejected, (state, action) => {
        state.reviewsLoading = false;
        state.error = action.payload;
      });
  },
});

export const {
  clearError,
  setSelectedDriver,
  clearDriverDetails,
  updateStatistics,
  toggleDriverStatusOptimistic,
  toggleDriverAvailabilityOptimistic,
  
} = driverSlice.actions;

export default driverSlice.reducer;
